import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminDoctorRoster.css";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-backend-v12.onrender.com").replace(/\/+$/, "");


type DoctorRow = {
  id: string;
  name?: string | null;
  email?: string | null;
  specialties?: string | null;
  medical_license_number?: string | null;
  country?: string | null;
  state?: string | null;
  website?: string | null;
  status?: string | null;
  bio?: string | null;
  photo_url?: string | null;
  retired?: boolean | null;
  is_active?: boolean | number | string | null;
  is_online?: boolean | number | string | null;
  is_available?: boolean | number | string | null;
  doctor_id?: number | string | null;
  advisor_id?: number | string | null;
  approved?: boolean | number | string | null;
  is_approved?: boolean | number | string | null;
  approval_status?: string | null;
  pin_last4?: string | null;
  pin_set_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  phone?: string | null;
  languages_json?: string | null;
  languages?: Array<{ language?: string; read?: string; write?: string; speak?: string }> | null;
  license_file_url?: string | null;
  license_file_name?: string | null;
  cv_file_url?: string | null;
  cv_file_name?: string | null;
  submitted_at?: string | null;
  reviewed_at?: string | null;
  rejection_reason?: string | null;
};

function clean(v: any) {
  return String(v ?? "").trim();
}

function truthy(v: any): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const s = v.toLowerCase().trim();
    return s === "1" || s === "true" || s === "yes" || s === "active" || s === "approved" || s === "on call";
  }
  return false;
}

function doctorIsActive(doc: DoctorRow): boolean {
  if (doc.is_active !== undefined && doc.is_active !== null) return truthy(doc.is_active);
  const status = clean(doc.status).toLowerCase();
  if (["inactive", "disabled", "retired", "unavailable", "off"].includes(status)) return false;
  return !truthy(doc.retired);
}

function doctorIsApproved(doc: DoctorRow): boolean {
  if (doc.approved !== undefined && doc.approved !== null) return truthy(doc.approved);
  if (doc.is_approved !== undefined && doc.is_approved !== null) return truthy(doc.is_approved);

  const approvalStatus = clean(doc.approval_status || doc.status).toLowerCase();

  if (["pending", "not approved", "rejected", "denied"].includes(approvalStatus)) {
    return false;
  }

  return true;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}


function adminToken() {
  return clean(
    localStorage.getItem("admin_token") ||
      localStorage.getItem("admin_access_token") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      ""
  ).replace(/^Bearer\s+/i, "");
}

function authHeaders(extra?: Record<string, string>) {
  const token = adminToken();
  return {
    ...(extra || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function backendJson(path: string, init: RequestInit = {}) {
  const headers = authHeaders((init.headers as Record<string, string>) || {});
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const msg = data?.detail || data?.message || `Request failed (${res.status})`;
    throw new Error(Array.isArray(msg) ? JSON.stringify(msg) : String(msg));
  }
  return data;
}

function absoluteFileUrl(value: any): string {
  const raw = clean(value);
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${API_BASE}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

function advisorLanguages(doc: DoctorRow) {
  if (Array.isArray(doc.languages)) return doc.languages;
  const raw = clean(doc.languages_json);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function AdminDoctorRoster() {
  const navigate = useNavigate();

  const [rows, setRows] = useState<DoctorRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState("");
  const [approvedFilter, setApprovedFilter] = useState<"all" | "true" | "false">("all");
  const [activeFilter, setActiveFilter] = useState<"all" | "true" | "false">("all");

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const selectedIds = useMemo(
    () => Object.keys(selected).filter((k) => selected[k]),
    [selected]
  );

  const [pinModal, setPinModal] = useState<{ open: boolean; doctor?: DoctorRow; pin?: string }>({
    open: false,
  });
  const [viewDoctor, setViewDoctor] = useState<DoctorRow | null>(null);

  const [rowBusy, setRowBusy] = useState<Record<string, boolean>>({});
  const [refreshTick, setRefreshTick] = useState(0);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("doctor_id");
    navigate("/");
  };

  const fetchRoster = async () => {
    setLoading(true);

    try {
      const data = await backendJson("/admin/doctors/roster");

      let list = ((data?.items || data?.doctors || data?.advisors || []) as DoctorRow[]).map((d: any) => ({
        ...d,
        id: String(d?.id ?? d?.doctor_id ?? d?.advisor_id ?? ""),
        specialties: d?.specialties ?? d?.specialty ?? d?.medical_field ?? "",
      })).filter((d) => clean(d.id));

      const search = clean(q).toLowerCase();
      if (search) {
        list = list.filter((d) =>
          `${d.name || ""} ${d.email || ""} ${d.specialties || ""} ${d.status || ""}`
            .toLowerCase()
            .includes(search)
        );
      }

      if (approvedFilter === "true") list = list.filter((d) => doctorIsApproved(d));
      if (approvedFilter === "false") list = list.filter((d) => !doctorIsApproved(d));
      if (activeFilter === "true") list = list.filter((d) => doctorIsActive(d));
      if (activeFilter === "false") list = list.filter((d) => !doctorIsActive(d));

      setRows(list);
    } catch (e: any) {
      alert(`Advisor roster fetch failed: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick]);

  const toggleAll = (checked: boolean) => {
    const next: Record<string, boolean> = {};

    if (checked) {
      rows.forEach((r) => {
        next[r.id] = true;
      });
    }

    setSelected(next);
  };

  const toggleOne = (id: string, checked: boolean) => {
    setSelected((prev) => ({ ...prev, [id]: checked }));
  };

  const setBusy = (id: string, busy: boolean) => {
    setRowBusy((prev) => ({ ...prev, [id]: busy }));
  };

  const openAdvisorReview = async (doc: DoctorRow) => {
    const id = doc.id;
    setBusy(id, true);
    try {
      const data = await backendJson(`/admin/doctors/${encodeURIComponent(id)}`);
      const detail = (data?.doctor || data?.advisor || data?.item || doc) as DoctorRow;
      setViewDoctor({ ...doc, ...detail, id: String((detail as any)?.id ?? id) });
    } catch (e: any) {
      alert(`Advisor review fetch failed: ${e?.message || e}`);
    } finally {
      setBusy(id, false);
    }
  };

  const approveAdvisor = async (doc: DoctorRow) => {
    const id = doc.id;
    if (!window.confirm(`Approve ${doc.name || "this advisor"}?`)) return;
    setBusy(id, true);
    try {
      await backendJson(`/admin/doctors/${encodeURIComponent(id)}/approve`, { method: "POST" });
      setViewDoctor(null);
      setRefreshTick((n) => n + 1);
      window.alert("Advisor approved.");
    } catch (e: any) {
      alert(`Approve advisor failed: ${e?.message || e}`);
    } finally {
      setBusy(id, false);
    }
  };

  const rejectAdvisor = async (doc: DoctorRow) => {
    const id = doc.id;
    const reason = window.prompt(`Reason for rejecting ${doc.name || "this advisor"} (optional):`) ?? "";
    if (!window.confirm(`Reject ${doc.name || "this advisor"}?`)) return;
    setBusy(id, true);
    try {
      await backendJson(`/admin/doctors/${encodeURIComponent(id)}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      setViewDoctor(null);
      setRefreshTick((n) => n + 1);
      window.alert("Advisor application rejected.");
    } catch (e: any) {
      alert(`Reject advisor failed: ${e?.message || e}`);
    } finally {
      setBusy(id, false);
    }
  };

  const issueOrResetPin = async (doc: DoctorRow) => {
    const id = doc.id;
    setBusy(id, true);

    try {
      const data = await backendJson(`/admin/doctors/${encodeURIComponent(id)}/issue_pin`, {
        method: "POST",
      });

      const pin = clean(data?.pin);
      const pinLast4 = clean(data?.pin_last4 || pin.slice(-4));
      const pinSetAt = clean(data?.pin_set_at || new Date().toISOString());

      setPinModal({ open: true, doctor: doc, pin });

      setRows((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, pin_last4: pinLast4, pin_set_at: pinSetAt, updated_at: pinSetAt }
            : r
        )
      );

      setRefreshTick((n) => n + 1);
    } catch (e: any) {
      alert(`Issue/Reset PIN failed: ${e?.message || e}`);
    } finally {
      setBusy(id, false);
    }
  };

  const setActive = async (doc: DoctorRow, isActive: boolean) => {
    const id = doc.id;

    const ok = window.confirm(
      isActive
        ? `Reactivate ${doc.name || "this advisor"}?`
        : `Deactivate ${doc.name || "this advisor"}?

This will NOT delete the advisor. They will remain in the roster and can be reactivated later.`
    );

    if (!ok) return;

    setBusy(id, true);

    try {
      const path = isActive ? "activate" : "deactivate";
      await backendJson(`/admin/doctors/${encodeURIComponent(id)}/${path}`, { method: "POST" });

      setRows((prev) =>
        prev.map((r) =>
          r.id === id
            ? {
                ...r,
                retired: !isActive,
                is_active: isActive,
                status: isActive ? "active" : "inactive",
                updated_at: new Date().toISOString(),
              }
            : r
        )
      );

      setRefreshTick((n) => n + 1);
    } catch (e: any) {
      alert(`Update active status failed: ${e?.message || e}`);
    } finally {
      setBusy(id, false);
    }
  };

  const deleteDoctor = async (doc: DoctorRow) => {
    const id = doc.id;

    const ok = window.confirm(
      `PERMANENTLY DELETE ${doc.name || "this advisor"}?

This cannot be undone. If you may need this advisor later, use Deactivate instead.`
    );

    if (!ok) return;

    const typed = window.prompt(`Type DELETE to permanently remove ${doc.name || "this advisor"}:`);
    if (typed !== "DELETE") return;

    setBusy(id, true);

    try {
      await backendJson(`/admin/doctors/${encodeURIComponent(id)}`, { method: "DELETE" });

      setRows((prev) => prev.filter((r) => r.id !== id));

      setSelected((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });

      setRefreshTick((n) => n + 1);
    } catch (e: any) {
      alert(`Delete advisor failed: ${e?.message || e}`);
    } finally {
      setBusy(id, false);
    }
  };

  const downloadResetPinsCsv = async () => {
    if (!selectedIds.length) {
      alert("Select at least 1 advisor.");
      return;
    }

    try {
      const selectedDoctors = rows.filter((r) => selectedIds.includes(r.id));
      const outputRows: Array<Record<string, string>> = [];

      for (const doc of selectedDoctors) {
        const data = await backendJson(`/admin/doctors/${encodeURIComponent(doc.id)}/issue_pin`, {
          method: "POST",
        });
        const pin = clean(data?.pin);

        outputRows.push({
          advisor_id: doc.id,
          name: clean(doc.name),
          email: clean(doc.email),
          pin,
        });
      }

      const header = ["advisor_id", "name", "email", "pin"];
      const csv = [
        header.join(","),
        ...outputRows.map((row) =>
          header.map((h) => `"${String(row[h] || "").replace(/"/g, '""')}"`).join(",")
        ),
      ].join("\n");

      downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), "advisor_reset_pins.csv");

      setSelected({});
      setRefreshTick((n) => n + 1);
    } catch (e: any) {
      alert(`CSV download failed: ${e?.message || e}`);
    }
  };

  const copyPin = async () => {
    try {
      if (pinModal.pin) {
        await navigator.clipboard.writeText(pinModal.pin);
      }
    } catch {
      // ignore
    }
  };

  return (
    <div className="docroster-page">
      <div className="docroster-panel">
        <div className="docroster-topbar">
          <div>
            <div className="docroster-title">Advisor Roster</div>
            <div className="docroster-subtitle">
              View approved advisors, issue/reset PINs, deactivate/reactivate accounts, view profiles, edit profiles, or permanently delete accounts.
            </div>
          </div>

          <div className="docroster-actions">
            <button className="btn" onClick={() => navigate("/dashboard")}>
              Return to Admin
            </button>

            <button className="btn" onClick={handleLogout}>
              Logout
            </button>

            <button
              className="btn"
              onClick={() => setRefreshTick((n) => n + 1)}
              disabled={loading}
              title="Reload roster from backend"
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>

            <button
              className="btn btn-primary"
              onClick={downloadResetPinsCsv}
              disabled={!selectedIds.length}
              title="Resets PINs for selected advisors and downloads a CSV. PIN is shown once."
            >
              Reset PINs (CSV)
            </button>
          </div>
        </div>

        <div className="docroster-filters">
          <div className="filter">
            <label>Search</label>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="name, email, specialty, status" />
          </div>

          <div className="filter">
            <label>Approved</label>
            <select value={approvedFilter} onChange={(e) => setApprovedFilter(e.target.value as any)}>
              <option value="all">All</option>
              <option value="true">Approved</option>
              <option value="false">Not approved</option>
            </select>
          </div>

          <div className="filter">
            <label>Status</label>
            <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value as any)}>
              <option value="all">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <button
            className="btn btn-secondary"
            onClick={() => setRefreshTick((n) => n + 1)}
            disabled={loading}
            title="Apply filters and reload"
          >
            Apply
          </button>
        </div>

        <div className="docroster-tablewrap">
          <table className="docroster-table">
            <thead>
              <tr>
                <th style={{ width: 44 }}>
                  <input
                    type="checkbox"
                    checked={rows.length > 0 && selectedIds.length === rows.length}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Specialty</th>
                <th>Approved</th>
                <th>Status</th>
                <th>PIN</th>
                <th>PIN Set</th>
                <th style={{ width: 500 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={9} className="empty">
                    No advisors found.
                  </td>
                </tr>
              ) : (
                rows.map((d) => {
                  const isActive = doctorIsActive(d);
                  const isApproved = doctorIsApproved(d);
                  const busy = !!rowBusy[d.id];

                  const pinLast4 = clean(d.pin_last4);
                  const pinSet = clean(d.pin_set_at);

                  return (
                    <tr key={d.id} className={!isActive ? "row-inactive" : ""}>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!selected[d.id]}
                          onChange={(e) => toggleOne(d.id, e.target.checked)}
                        />
                      </td>

                      <td className="mono">{d.name || ""}</td>
                      <td className="mono">{d.email || ""}</td>
                      <td className="mono">{d.specialties || ""}</td>

                      <td>
                        <span className={`pill ${isApproved ? "pill-ok" : "pill-warn"}`}>
                          {isApproved ? "YES" : "NO"}
                        </span>
                      </td>

                      <td>
                        <span className={`pill ${isActive ? "pill-ok" : "pill-bad"}`}>
                          {isActive ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>

                      <td className="mono">{pinLast4 ? `••••${pinLast4}` : ""}</td>
                      <td className="mono">{pinSet || ""}</td>

                      <td className="actions">
                        <button
                          className="btn btn-small"
                          disabled={busy}
                          onClick={() => openAdvisorReview(d)}
                          title="Open advisor profile"
                        >
                          View
                        </button>

                        {!isApproved && clean(d.approval_status || d.status).toLowerCase() !== "rejected" && (
                          <>
                            <button
                              className="btn btn-small btn-success"
                              disabled={busy}
                              onClick={() => approveAdvisor(d)}
                              title="Approve this pending advisor application"
                            >
                              Approve
                            </button>

                            <button
                              className="btn btn-small btn-danger"
                              disabled={busy}
                              onClick={() => rejectAdvisor(d)}
                              title="Reject this pending advisor application"
                            >
                              Reject
                            </button>
                          </>
                        )}

                        <button
                          className="btn btn-small btn-secondary"
                          disabled={busy}
                          onClick={() => navigate(`/edit-doctor/${d.id}`, { state: { doctor: d } })}
                          title="Edit advisor profile"
                        >
                          Edit
                        </button>

                        <button
                          className="btn btn-small"
                          disabled={busy}
                          onClick={() => issueOrResetPin(d)}
                          title="Issues a new 6-digit Advisor PIN. Returned once."
                        >
                          {pinSet ? "Reissue PIN" : "Issue PIN"}
                        </button>

                        {isActive ? (
                          <button
                            className="btn btn-small btn-danger"
                            disabled={busy}
                            onClick={() => setActive(d, false)}
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            className="btn btn-small btn-success"
                            disabled={busy}
                            onClick={() => setActive(d, true)}
                          >
                            Reactivate
                          </button>
                        )}

                        <button
                          className="btn btn-small btn-danger"
                          disabled={busy}
                          onClick={() => deleteDoctor(d)}
                          title="Permanently deletes this advisor. Use Deactivate if you want to keep the record."
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {viewDoctor && (
        <div className="modal-backdrop" onClick={() => setViewDoctor(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Advisor Profile</div>
            <div className="modal-body">
              <div className="modal-row"><div className="label">Name</div><div className="value">{viewDoctor.name || "—"}</div></div>
              <div className="modal-row"><div className="label">Email</div><div className="value">{viewDoctor.email || "—"}</div></div>
              <div className="modal-row"><div className="label">Phone</div><div className="value">{viewDoctor.phone || "—"}</div></div>
              <div className="modal-row"><div className="label">Specialty</div><div className="value">{viewDoctor.specialties || "—"}</div></div>
              <div className="modal-row"><div className="label">License / Certification #</div><div className="value">{viewDoctor.medical_license_number || "—"}</div></div>
              <div className="modal-row"><div className="label">Country / State</div><div className="value">{[viewDoctor.country, viewDoctor.state].filter(Boolean).join(" / ") || "—"}</div></div>
              <div className="modal-row"><div className="label">Website</div><div className="value">{viewDoctor.website || "—"}</div></div>
              <div className="modal-row"><div className="label">Status</div><div className="value">{viewDoctor.approval_status || viewDoctor.status || "—"}</div></div>
              <div className="modal-row"><div className="label">Approved</div><div className="value">{doctorIsApproved(viewDoctor) ? "Yes" : "No"}</div></div>
              <div className="modal-row"><div className="label">Submitted</div><div className="value">{viewDoctor.submitted_at || viewDoctor.created_at || "—"}</div></div>

              <div className="modal-row">
                <div className="label">Languages</div>
                <div className="value">
                  {advisorLanguages(viewDoctor).length ? (
                    <div>
                      {advisorLanguages(viewDoctor).map((lang: any, index: number) => (
                        <div key={`${lang?.language || "language"}-${index}`} style={{ marginBottom: 4 }}>
                          <b>{lang?.language || "—"}</b>
                          {" — "}Read: {lang?.read || "—"}, Write: {lang?.write || "—"}, Speak: {lang?.speak || "—"}
                        </div>
                      ))}
                    </div>
                  ) : "—"}
                </div>
              </div>

              <div className="modal-row">
                <div className="label">License / Certification</div>
                <div className="value">
                  {viewDoctor.license_file_url ? (
                    <a href={absoluteFileUrl(viewDoctor.license_file_url)} target="_blank" rel="noreferrer">
                      Open {viewDoctor.license_file_name || "license document"}
                    </a>
                  ) : "—"}
                </div>
              </div>

              <div className="modal-row">
                <div className="label">CV / Resume</div>
                <div className="value">
                  {viewDoctor.cv_file_url ? (
                    <a href={absoluteFileUrl(viewDoctor.cv_file_url)} target="_blank" rel="noreferrer">
                      Open {viewDoctor.cv_file_name || "CV / Resume"}
                    </a>
                  ) : "—"}
                </div>
              </div>

              {viewDoctor.rejection_reason && (
                <div className="modal-row"><div className="label">Rejection Reason</div><div className="value">{viewDoctor.rejection_reason}</div></div>
              )}

              <div className="modal-row"><div className="label">Bio</div><div className="value">{viewDoctor.bio || "—"}</div></div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setViewDoctor(null)}>Close</button>

              {!doctorIsApproved(viewDoctor) && clean(viewDoctor.approval_status || viewDoctor.status).toLowerCase() !== "rejected" && (
                <>
                  <button className="btn btn-success" onClick={() => approveAdvisor(viewDoctor)}>
                    Approve
                  </button>
                  <button className="btn btn-danger" onClick={() => rejectAdvisor(viewDoctor)}>
                    Reject
                  </button>
                </>
              )}

              <button
                className="btn btn-secondary"
                onClick={() => navigate(`/edit-doctor/${viewDoctor.id}`, { state: { doctor: viewDoctor } })}
              >
                Edit Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {pinModal.open && (
        <div className="modal-backdrop" onClick={() => setPinModal({ open: false })}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Advisor PIN Issued</div>

            <div className="modal-body">
              <div className="modal-row">
                <div className="label">Advisor</div>
                <div className="value">
                  {(pinModal.doctor?.name || "").trim()}{" "}
                  <span className="muted">{pinModal.doctor?.email || ""}</span>
                </div>
              </div>

              <div className="pin-box">
                <div className="pin">{pinModal.pin || ""}</div>

                <button className="btn btn-small btn-primary" onClick={copyPin}>
                  Copy
                </button>
              </div>

              <div className="muted">
                This PIN is shown <b>one time</b>. Save it now or reissue later.
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn" onClick={() => setPinModal({ open: false })}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}